import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { postJSON } from '@/lib/api'

export interface SchedulableBlast {
  id: number
  subject: string
  status: 'draft' | 'scheduled' | 'sending' | 'paused' | 'sent' | 'canceled'
  scheduled_for: string | null
}

export function ScheduleDialog({
  blast,
  onClose,
}: {
  blast: SchedulableBlast | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [scheduledFor, setScheduledFor] = React.useState('')
  React.useEffect(() => {
    setScheduledFor(blast?.scheduled_for?.slice(0, 16) ?? '')
  }, [blast])
  const schedule = useMutation({
    mutationFn: () =>
      postJSON(`/api/v1/blasts/${blast?.id}/schedule`, { scheduled_for: scheduledFor }),
    onSuccess: () => {
      onClose()
      return queryClient.invalidateQueries({ queryKey: ['blasts'] })
    },
  })
  return (
    <Dialog open={blast !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {blast?.status === 'scheduled' ? 'Reschedule Blast' : 'Schedule Blast'}
          </DialogTitle>
          <DialogDescription>The date and time use your Association clock.</DialogDescription>
        </DialogHeader>
        {schedule.error ? <Alert>{schedule.error.message}</Alert> : null}
        <div className="space-y-1.5">
          <Label htmlFor="blast-scheduled-for">Send at</Label>
          <Input
            id="blast-scheduled-for"
            type="datetime-local"
            value={scheduledFor}
            onChange={(event) => setScheduledFor(event.target.value)}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Back
          </Button>
          <Button
            type="button"
            disabled={!scheduledFor || schedule.isPending}
            onClick={() => schedule.mutate()}
          >
            Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function CancelScheduleDialog({
  blast,
  pending,
  onClose,
  onCancel,
}: {
  blast: SchedulableBlast | null
  pending: boolean
  onClose: () => void
  onCancel: (blast: SchedulableBlast) => void
}) {
  return (
    <Dialog open={blast !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel scheduled Blast?</DialogTitle>
          <DialogDescription>
            &quot;{blast?.subject}&quot; will remain in permanent history and will not send.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Keep scheduled
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={pending}
            onClick={() => blast && onCancel(blast)}
          >
            Cancel Blast
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
